import React, { useState } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import {
  ScanSearch, RefreshCw, CheckCircle2, AlertTriangle, Database,
  Users, Briefcase, Building2, FolderKanban, MapPin, Wand2, Archive, X,
} from 'lucide-react';

const COLLECTION_META = {
  users: { label: 'Users', icon: Users },
  jobs: { label: 'Jobs / Shifts', icon: Briefcase },
  company_profiles: { label: 'Company Profiles', icon: Building2 },
  programs: { label: 'Programs', icon: FolderKanban },
  locations: { label: 'Locations', icon: MapPin },
};

export const DataQualityPanel = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [cleaning, setCleaning] = useState(null); // 'auto_trim' | 'archive_test' | row key
  const [confirmAction, setConfirmAction] = useState(null); // { action, count }

  const runScan = async (silent = false) => {
    setLoading(true);
    try {
      const res = await apiCall('/api/admin/data-quality/report');
      setReport(res);
      if (!silent) toast.success(`Scan complete — ${res.total_flagged} issue${res.total_flagged === 1 ? '' : 's'} found across ${res.total_scanned} records`);
    } catch (e) {
      toast.error(e.message || 'Scan failed');
    } finally {
      setLoading(false);
    }
  };

  const runCleanup = async (action, targets = null) => {
    setCleaning(targets ? `${targets[0].collection}-${targets[0].record_id}-${targets[0].field}` : action);
    try {
      const res = await apiCall('/api/admin/data-quality/cleanup', {
        method: 'POST',
        body: JSON.stringify(targets ? { action, targets } : { action }),
      });
      if (action === 'auto_trim') {
        toast.success(`Auto-trim complete — ${res.fixed_count} value${res.fixed_count === 1 ? '' : 's'} fixed${res.skipped_count ? `, ${res.skipped_count} skipped` : ''}`);
      } else {
        toast.success(`Archived ${res.archived_count} TEST_ record${res.archived_count === 1 ? '' : 's'}${res.skipped_count ? ` (${res.skipped_count} skipped)` : ''}`);
      }
      await runScan(true);
    } catch (e) {
      toast.error(e.message || 'Cleanup failed');
    } finally {
      setCleaning(null);
      setConfirmAction(null);
    }
  };

  const issues = (report?.issues || []).filter((i) => filter === 'all' || i.collection === filter);
  const healthColor = !report ? '' : report.health_score >= 98 ? 'text-emerald-600' : report.health_score >= 90 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="space-y-6" data-testid="data-quality-panel">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-cyan-50 flex items-center justify-center">
            <ScanSearch className="w-6 h-6 text-cyan-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Data Quality Report</h2>
            <p className="text-sm text-slate-500">
              Scans existing records against the validation framework and flags legacy junk entries.
            </p>
          </div>
        </div>
        <button
          onClick={() => runScan()}
          disabled={loading}
          className="h-11 px-5 rounded-full bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="data-quality-run-scan-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Scanning…' : report ? 'Re-scan' : 'Run Scan'}
        </button>
      </div>

      {!report && !loading && (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center" data-testid="data-quality-empty-state">
          <Database className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Run a scan to audit users, jobs, company profiles, programs and locations.</p>
        </div>
      )}

      {report && (
        <>
          {/* Health + per-collection summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="data-quality-health-score">
              <p className="text-xs font-medium text-slate-500 uppercase">Health Score</p>
              <p className={`text-2xl font-bold ${healthColor}`}>{report.health_score}%</p>
              <p className="text-xs text-slate-400">{report.total_scanned} records scanned</p>
            </div>
            {Object.entries(report.summary).map(([key, s]) => {
              const meta = COLLECTION_META[key] || { label: key, icon: Database };
              const Icon = meta.icon;
              return (
                <div key={key} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`data-quality-summary-${key}`}>
                  <div className="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase">
                    <Icon className="w-3.5 h-3.5" /> {meta.label}
                  </div>
                  <p className={`text-2xl font-bold ${s.flagged ? 'text-amber-600' : 'text-emerald-600'}`}>{s.flagged}</p>
                  <p className="text-xs text-slate-400">flagged of {s.scanned}</p>
                </div>
              );
            })}
          </div>

          {/* Clean up actions */}
          {(report.fixable_count > 0 || report.test_record_count > 0) && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-3" data-testid="data-quality-cleanup-bar">
              <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-cyan-600" /> Clean up
              </span>
              {report.fixable_count > 0 && (
                <button
                  onClick={() => setConfirmAction({ action: 'auto_trim', count: report.fixable_count })}
                  disabled={!!cleaning}
                  className="h-9 px-4 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="data-quality-auto-trim-btn"
                >
                  <Wand2 className="w-3.5 h-3.5" />
                  Auto-trim {report.fixable_count} fixable value{report.fixable_count === 1 ? '' : 's'}
                </button>
              )}
              {report.test_record_count > 0 && (
                <button
                  onClick={() => setConfirmAction({ action: 'archive_test', count: report.test_record_count })}
                  disabled={!!cleaning}
                  className="h-9 px-4 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="data-quality-archive-test-btn"
                >
                  <Archive className="w-3.5 h-3.5" />
                  Archive {report.test_record_count} TEST_ record{report.test_record_count === 1 ? '' : 's'}
                </button>
              )}
              <span className="text-xs text-slate-400">
                Auto-trim fixes whitespace issues in place. Archiving deactivates flagged TEST_ records (reversible).
              </span>
            </div>
          )}

          {/* Issues */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                {report.total_flagged === 0
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  : <AlertTriangle className="w-5 h-5 text-amber-500" />}
                {report.total_flagged === 0 ? 'No data quality issues found' : `${report.total_flagged} flagged entr${report.total_flagged === 1 ? 'y' : 'ies'}`}
                {report.issues_capped && <span className="text-xs font-normal text-slate-400">(showing first 500)</span>}
              </h3>
              {report.total_flagged > 0 && (
                <div className="flex flex-wrap gap-2">
                  {['all', ...Object.keys(report.summary).filter((k) => report.summary[k].flagged > 0)].map((k) => (
                    <button
                      key={k}
                      onClick={() => setFilter(k)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === k ? 'bg-cyan-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid={`data-quality-filter-${k}`}
                    >
                      {k === 'all' ? 'All' : (COLLECTION_META[k]?.label || k)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {report.total_flagged > 0 && (
              <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs text-slate-500 uppercase sticky top-0">
                    <tr>
                      <th className="px-4 py-3">Collection</th>
                      <th className="px-4 py-3">Record</th>
                      <th className="px-4 py-3">Field</th>
                      <th className="px-4 py-3">Current Value</th>
                      <th className="px-4 py-3">Issue</th>
                      <th className="px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody data-testid="data-quality-issues-table">
                    {issues.map((i) => {
                      const rowKey = `${i.collection}-${i.record_id}-${i.field}`;
                      return (
                        <tr key={rowKey} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{COLLECTION_META[i.collection]?.label || i.collection}</td>
                          <td className="px-4 py-3 font-medium text-slate-900 max-w-[220px]" title={i.record_id}>
                            <span className="flex items-center gap-1.5">
                              <span className="truncate">{i.record_label}</span>
                              {i.is_test && <span className="shrink-0 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" data-testid={`test-badge-${rowKey}`}>TEST</span>}
                            </span>
                          </td>
                          <td className="px-4 py-3"><code className="text-xs text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">{i.field}</code></td>
                          <td className="px-4 py-3 text-red-600 max-w-[240px] truncate" title={i.value}>{i.value}</td>
                          <td className="px-4 py-3 text-slate-600">{i.issue}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {i.fixable ? (
                              <button
                                onClick={() => runCleanup('auto_trim', [{ collection: i.collection, record_id: i.record_id, field: i.field }])}
                                disabled={!!cleaning}
                                className="h-7 px-2.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition-colors disabled:opacity-50 flex items-center gap-1"
                                title={`Fix to: ${i.suggested}`}
                                data-testid={`fix-btn-${rowKey}`}
                              >
                                <Wand2 className="w-3 h-3" />
                                {cleaning === rowKey ? 'Fixing…' : 'Fix'}
                              </button>
                            ) : (
                              <span className="text-xs text-slate-400">Manual review</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Confirm cleanup modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="cleanup-confirm-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-bold text-slate-900 flex items-center gap-2">
                {confirmAction.action === 'auto_trim'
                  ? <><Wand2 className="w-5 h-5 text-emerald-600" /> Auto-trim fixable values</>
                  : <><Archive className="w-5 h-5 text-amber-600" /> Archive TEST_ records</>}
              </h3>
              <button onClick={() => setConfirmAction(null)} className="text-slate-400 hover:text-slate-600" data-testid="cleanup-confirm-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-5">
              {confirmAction.action === 'auto_trim'
                ? `This will trim leading/trailing whitespace and collapse repeated spaces on ${confirmAction.count} flagged value${confirmAction.count === 1 ? '' : 's'}, then re-validate. Only values that pass validation after trimming are updated.`
                : `This will archive ${confirmAction.count} record${confirmAction.count === 1 ? '' : 's'} whose name or value starts with "TEST_". Archived users can no longer sign in; jobs, programs and locations are hidden from active views. Admin accounts are never archived. This is reversible from each record's management panel.`}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmAction(null)}
                className="h-10 px-4 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition-colors"
                data-testid="cleanup-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={() => runCleanup(confirmAction.action)}
                disabled={!!cleaning}
                className={`h-10 px-5 rounded-full text-white text-sm font-semibold transition-colors disabled:opacity-50 ${confirmAction.action === 'auto_trim' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-amber-500 hover:bg-amber-600'}`}
                data-testid="cleanup-confirm-btn"
              >
                {cleaning ? 'Working…' : confirmAction.action === 'auto_trim' ? `Fix ${confirmAction.count} value${confirmAction.count === 1 ? '' : 's'}` : `Archive ${confirmAction.count} record${confirmAction.count === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataQualityPanel;
